/**
 * In-thread destructive-approval affordance for Shogun `platform_call`
 * (DESKTOP arm). On desktop the daemon gates a
 * destructive call server-side and reports it back as `confirmation_required`;
 * this card surfaces the pending change and lets the user Approve (POST the
 * server-stamped idempotency key back to the daemon so the re-issued call
 * executes) or Dismiss (do nothing).
 *
 * PROPS-DRIVEN — it fetches nothing. The host (`ChatView`) owns the SDK call,
 * the `isApproving` flag, and any error string; this component only renders.
 */

import type { ExperienceMode, PlatformApprovalRequest } from '@localagent/sdk';
import { namedTarget, NO_NAMED_TARGET } from '../chat/named-target';
import { PlatformCallArgumentsPreview } from './PlatformCallArgumentsPreview';
import { startsAProgramOnThisComputer } from '../../shogun/platform-call-preview';

interface ShogunApprovalCardProps {
  request: PlatformApprovalRequest;
  onApprove: () => void | Promise<void>;
  onDismiss: () => void;
  isApproving?: boolean;
  /** Error surfaced from a failed Approve; never swallowed. */
  error?: string | null;
  mode?: ExperienceMode;
}

export function ShogunApprovalCard({
  request,
  onApprove,
  onDismiss,
  isApproving,
  error,
  mode,
}: ShogunApprovalCardProps) {
  const isNormal = mode !== 'advanced';
  const target = namedTarget(request.arguments);
  return (
    <div
      data-testid="shogun-approval-card"
      className="rounded bg-warning-subtle p-2.5"
    >
      {isNormal ? (
        <p className="mb-1 text-caption font-medium text-foreground">
          {target ? (
            <>
              Shogun wants to continue with{' '}
              <span data-testid="shogun-approval-target">{target}</span>.
            </>
          ) : (
            <>
              Shogun wants to continue.{' '}
              <span data-testid="shogun-approval-no-target">{NO_NAMED_TARGET}</span>
            </>
          )}
        </p>
      ) : null}
      {isNormal && startsAProgramOnThisComputer(request.resource, request.method) && (
        <PlatformCallArgumentsPreview
          resource={request.resource}
          method={request.method}
          args={request.arguments}
          testId="shogun-approval-preview"
          className="mb-2 max-h-48 overflow-auto rounded bg-muted p-2 text-micro"
        />
      )}
      {isNormal ? null : (
        <>
          <p className="mb-1 text-caption font-medium text-foreground">
            Shogun wants to run{' '}
            <code
              data-testid="shogun-approval-action"
              className="rounded bg-muted px-1 py-0.5 text-micro"
            >
              {request.resource}.{request.method}
            </code>
          </p>
          <PlatformCallArgumentsPreview
            resource={request.resource}
            method={request.method}
            args={request.arguments}
            testId="shogun-approval-preview"
            className="mb-2 max-h-48 overflow-auto rounded bg-muted p-2 text-micro"
          />
        </>
      )}
      <p className="mb-2 text-micro text-warning-text">
        This will make a permanent change that is not automatically reversible.
      </p>

      {error && (
        <p data-testid="shogun-approval-error" className="mb-2 text-micro text-destructive">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="shogun-approval-approve"
          disabled={isApproving}
          className="rounded bg-warning-solid px-3 py-1 text-micro font-medium text-warning-solid-foreground hover:bg-warning-solid/90 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            void onApprove();
          }}
        >
          {isApproving ? 'Approving…' : 'Approve'}
        </button>
        <button
          type="button"
          data-testid="shogun-approval-dismiss"
          disabled={isApproving}
          className="rounded px-3 py-1 text-micro text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
